import React, { useState, useEffect } from 'react';
import { ordersAPI, salesAPI } from '../services/api';
import { 
  Plus, Edit, Trash2,
  CheckCircle, XCircle, Clock, Package, Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [formData, setFormData] = useState({
    order_number: '', customer_id: '', order_date: '', delivery_date: '',
    status: 'pending', notes: '', total_amount: 0, paid_amount: 0, items: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, customersRes, productsRes, statsRes] = await Promise.all([
        ordersAPI.getOrders({}),
        salesAPI.getCustomers({ limit: 1000 }), // Tüm müşterileri çek
        salesAPI.getProducts({ limit: 1000 }),  // Tüm ürünleri çek
        ordersAPI.getOrderStats()
      ]);
      
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : (ordersRes.data.orders || []));
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : (customersRes.data.customers || []));
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : (productsRes.data.products || []));
      setStats(statsRes.data);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateOrderNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `SIP-${year}${month}${day}-${random}`;
  };

  const handleCreateNew = () => {
    setEditingOrder(null);
    setFormData({
      order_number: generateOrderNumber(),
      customer_id: '',
      order_date: new Date().toISOString().split('T')[0],
      delivery_date: '',
      status: 'pending',
      notes: '',
      total_amount: 0,
      paid_amount: 0,
      items: []
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOrder) {
        await ordersAPI.updateOrder(editingOrder.id, formData);
      } else {
        await ordersAPI.createOrder(formData);
      }
      setShowModal(false);
      setEditingOrder(null);
      setFormData({ order_number: '', customer_id: '', order_date: '', delivery_date: '', status: 'pending', notes: '', total_amount: 0, paid_amount: 0, items: [] });
      fetchData();
      toast.success(editingOrder ? 'Sipariş güncellendi' : 'Sipariş oluşturuldu');
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      toast.error('Sipariş kaydedilemedi');
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData(order);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu siparişi silmek istediğinize emin misiniz?')) {
      try {
        await ordersAPI.deleteOrder(id);
        fetchData();
      } catch (error) {
        console.error('Silme hatası:', error);
      }
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await ordersAPI.updateOrderStatus(id, status);
      fetchData();
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
    }
  };

  const handleViewDetail = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', product_name: '', quantity: 1, unit_price: 0, total_price: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'product_id') {
      const product = products.find(p => p.id === parseInt(value));
      if (product) {
        newItems[index].product_name = product.name;
        newItems[index].unit_price = product.price;
        newItems[index].total_price = product.price * newItems[index].quantity;
      }
    }
    if (field === 'quantity') {
      newItems[index].total_price = newItems[index].unit_price * value;
    }
    
    setFormData({ ...formData, items: newItems, total_amount: newItems.reduce((sum, item) => sum + item.total_price, 0) });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems, total_amount: newItems.reduce((sum, item) => sum + item.total_price, 0) });
  };

  const filteredOrders = orders.filter(o => 
    o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(o => !statusFilter || o.status === statusFilter);

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Bekliyor', icon: Clock },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'İşleniyor', icon: Package },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Tamamlandı', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'İptal', icon: XCircle }
    };
    const s = statusMap[status] || statusMap.pending;
    return <span className={`px-2 py-1 rounded-full text-xs ${s.bg} ${s.text}`}>{s.label}</span>;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Sipariş Yönetimi</h1>
        <button
          onClick={handleCreateNew}
          data-help-target="orders-create"
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <Plus size={18} />
          Yeni Sipariş
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Toplam</p>
          <p className="text-2xl font-bold">{stats.total || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Bekliyor</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">İşleniyor</p>
          <p className="text-2xl font-bold text-blue-600">{stats.processing || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Tamamlandı</p>
          <p className="text-2xl font-bold text-green-600">{stats.completed || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Bugün</p>
          <p className="text-2xl font-bold">{stats.today_orders || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Tutar</p>
          <p className="text-2xl font-bold">{formatCurrency(stats.total_amount)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <input
          type="text"
          placeholder="Sipariş no veya müşteri ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded-lg"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded-lg">
          <option value="">Tüm Durumlar</option>
          <option value="pending">Bekliyor</option>
          <option value="processing">İşleniyor</option>
          <option value="completed">Tamamlandı</option>
          <option value="cancelled">İptal</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-10">Yükleniyor...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Sipariş No</th>
                <th className="px-4 py-3 text-left">Müşteri</th>
                <th className="px-4 py-3 text-left">Tarih</th>
                <th className="px-4 py-3 text-left">Teslim Tarihi</th>
                <th className="px-4 py-3 text-right">Tutar</th>
                <th className="px-4 py-3 text-center">Durum</th>
                <th className="px-4 py-3 text-center">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => (
                <tr key={index} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{order.order_number}</td>
                  <td className="px-4 py-3">{order.customer_name || '-'}</td>
                  <td className="px-4 py-3">{order.order_date}</td>
                  <td className="px-4 py-3">{order.delivery_date || '-'}</td>
                  <td className="px-4 py-3 text-right font-medium">₺{order.total_amount?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(order.status)}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleViewDetail(order)} className="text-gray-600 hover:text-gray-800 mr-2" title="Detay"><Eye size={18} /></button>
                    <button onClick={() => handleEdit(order)} className="text-blue-600 hover:text-blue-800 mr-2" title="Düzenle"><Edit size={18} /></button>
                    <button onClick={() => handleDelete(order.id)} className="text-red-600 hover:text-red-800" title="Sil"><Trash2 size={18} /></button>
                  </td>
                </tr>
              ))}
              {filteredOrders.length === 0 && (
                <tr><td colSpan="7" className="text-center py-8 text-gray-500">Sipariş bulunamadı</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editingOrder ? 'Sipariş Düzenle' : 'Yeni Sipariş'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">Sipariş No *</label><input type="text" required value={formData.order_number} onChange={e => setFormData({...formData, order_number: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Müşteri *</label><select required value={formData.customer_id} onChange={e => setFormData({...formData, customer_id: e.target.value})} className="w-full border rounded px-3 py-2"><option value="">Seçin</option>{(customers || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                <div><label className="block text-sm font-medium mb-1">Sipariş Tarihi *</label><input type="date" required value={formData.order_date} onChange={e => setFormData({...formData, order_date: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Teslim Tarihi</label><input type="date" value={formData.delivery_date} onChange={e => setFormData({...formData, delivery_date: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Durum</label><select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full border rounded px-3 py-2"><option value="pending">Bekliyor</option><option value="processing">İşleniyor</option><option value="completed">Tamamlandı</option><option value="cancelled">İptal</option></select></div>
                <div><label className="block text-sm font-medium mb-1">Toplam Tutar</label><input type="number" value={formData.total_amount} onChange={e => setFormData({...formData, total_amount: parseFloat(e.target.value)})} className="w-full border rounded px-3 py-2" /></div>
                <div className="col-span-2"><label className="block text-sm font-medium mb-1">Notlar</label><textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full border rounded px-3 py-2" rows="2" /></div>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">Sipariş Kalemleri</label>
                  <button type="button" onClick={addItem} className="text-blue-600 text-sm">+ Kalem Ekle</button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2 items-center">
                    <select value={item.product_id} onChange={e => updateItem(index, 'product_id', e.target.value)} className="flex-1 border rounded px-2 py-1"><option value="">Ürün Seç</option>{(products || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
                    <input type="number" value={item.quantity} onChange={e => updateItem(index, 'quantity', e.target.value)} className="w-20 border rounded px-2 py-1" />
                    <span className="w-24 text-right">₺{item.total_price}</span>
                    <button type="button" onClick={() => removeItem(index)} className="text-red-600"><XCircle size={18} /></button>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded">İptal</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Sipariş Detay - {selectedOrder.order_number}</h2>
            <div className="space-y-2 mb-4">
              <p><strong>Müşteri:</strong> {selectedOrder.customer_name}</p>
              <p><strong>Sipariş Tarihi:</strong> {selectedOrder.order_date}</p>
              <p><strong>Teslim Tarihi:</strong> {selectedOrder.delivery_date || '-'}</p>
              <p><strong>Durum:</strong> {getStatusBadge(selectedOrder.status)}</p>
              <p><strong>Toplam:</strong> ₺{selectedOrder.total_amount?.toLocaleString()}</p>
              <p><strong>Notlar:</strong> {selectedOrder.notes || '-'}</p>
            </div>
            <div className="flex justify-between items-center">
              <select value={selectedOrder.status} onChange={e => handleStatusChange(selectedOrder.id, e.target.value)} className="border rounded px-3 py-2">
                <option value="pending">Bekliyor</option>
                <option value="processing">İşleniyor</option>
                <option value="completed">Tamamlandı</option>
                <option value="cancelled">İptal</option>
              </select>
              <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 bg-gray-600 text-white rounded">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
